import { UserPermissionsResponse } from '@/types/auth.types';
import { updateRolePermissions } from '@/constant/rolePermissions';

export const mapApiPermissionsToRolePermissions = (
  apiPermissions: UserPermissionsResponse
): Record<string, string[]> => {
  const rolePermissions: Record<string, string[]> = {};

  const moduleSet = new Set<string>();
  const permissionKeySet = new Set<string>();

  apiPermissions.forEach((role) => {
    const roleKey = role.name || `role_${role.id}`;

    rolePermissions[roleKey] = ['/'];

    const uniquePaths = new Set<string>();

    let hasRoleAndPermissionModule = false;

    let hasActivityLogModule = false;

    role.role_has_permissions.forEach((permission) => {
      if (permission.permissions) {
        const permKey = permission.permissions.unique_key;
        const moduleKey = permission.permissions.module?.unique_key;
        const moduleName = permission.permissions.module?.name;

        if (moduleKey) moduleSet.add(moduleKey);
        if (permKey) permissionKeySet.add(permKey);

        if (
          moduleKey === 'activity_log' ||
          permKey?.startsWith('activity_log')
        ) {
          hasActivityLogModule = true;
        }

        if (
          moduleKey === 'role_and_permission' ||
          moduleName === 'Role And Permission' ||
          permKey === 'create_role' ||
          permKey === 'modify_role' ||
          permKey === 'delete_role' ||
          permKey === 'view_role_permission' ||
          permKey === 'modify_role_permission'
        ) {
          hasRoleAndPermissionModule = true;

          uniquePaths.add('/roles-management');
        }

        const path = mapPermissionToRoute(permKey, moduleKey);
        if (path && path !== '/') {
          uniquePaths.add(path);
        }

        // Platform Settings module should allow both platform settings and PSP fee config pages
        if (moduleKey === 'platform_setting') {
          uniquePaths.add('/platform-setting');
          uniquePaths.add('/psp-fee-config');
        }
      }
    });

    if (hasActivityLogModule) {
      uniquePaths.add('/user-logs');
      uniquePaths.add('/export-logs');
      uniquePaths.add('/delete-logs');
    }
    uniquePaths.forEach((path) => rolePermissions[roleKey].push(path));

    if (
      hasRoleAndPermissionModule &&
      !rolePermissions[roleKey].includes('/roles-management')
    ) {
      rolePermissions[roleKey].push('/roles-management');
    }
  });

  return rolePermissions;
};

const mapPermissionToRoute = (
  permissionKey: string,
  moduleKey: string
): string | null => {
  const permissionToRouteMap: Record<string, string> = {
    // Module: User Management
    view_users: '/user-management',
    create_user: '/user-management',
    edit_user: '/user-management',
    delete_user: '/user-management',
    user_management_create: '/user-management',
    user_management_read: '/user-management',
    user_management_update: '/user-management',
    user_management_delete: '/user-management',

    // Module: Boat Management
    view_boats: '/boat-management',
    create_boat: '/boat-management',
    edit_boat: '/boat-management',
    delete_boat: '/boat-management',
    boat_management_create: '/boat-management',
    boat_management_read: '/boat-management',
    boat_management_update: '/boat-management',
    boat_management_delete: '/boat-management',

    // Module: Subscription Management
    subscription_management_create: '/subscription-management',
    subscription_management_read: '/subscription-management',
    subscription_management_update: '/subscription-management',
    subscription_management_delete: '/subscription-management',

    entitlement_grant_management_create: '/configuration/entitlement-grants',
    entitlement_grant_management_read: '/configuration/entitlement-grants',
    entitlement_grant_management_update: '/configuration/entitlement-grants',
    entitlement_grant_management_delete: '/configuration/entitlement-grants',

    // Module: Invoice Management
    invoice_management_create: '/subscription-transactions',
    invoice_management_read: '/subscription-transactions',
    invoice_management_update: '/subscription-transactions',
    invoice_management_delete: '/subscription-transactions',

    // Module: Berth Management
    view_berths: '/berth-management',
    create_berth: '/berth-management',
    edit_berth: '/berth-management',
    delete_berth: '/berth-management',
    berth_management_create: '/berth-management',
    berth_management_read: '/berth-management',
    berth_management_update: '/berth-management',
    berth_management_delete: '/berth-management',

    // Module: Harbor Management
    view_harbors: '/habor-management',
    create_harbor: '/harbors/create',
    bulk_import_harbors: '/harbors/bulk-import',
    edit_harbor: '/harbors/edit',
    view_harbor_details: '/harbors/details',
    view_harbor: '/harbors/view',
    harbor_management_create: '/habor-management',
    harbor_management_read: '/habor-management',
    harbor_management_update: '/habor-management',
    harbor_management_delete: '/habor-management',

    // Module: Role and Permission
    create_role: '/roles-management',
    modify_role: '/roles-management',
    delete_role: '/roles-management',
    view_role_permission: '/roles-management',
    modify_role_permission: '/roles-management',

    // Module: Activity Log
    activity_log_create: '/user-logs',
    activity_log_read: '/user-logs',
    activity_log_update: '/user-logs',
    activity_log_delete: '/user-logs',
    activity_log_export: '/export-logs',

    // Module: Club Management
    club_management_create: '/club-management',
    club_management_read: '/club-management',
    club_management_update: '/club-management',
    club_management_delete: '/club-management',
    club_management_admin_candidates_read: '/club-management',

    // Module: Berth Category Management
    berth_category_management_create: '/berth-category-management',
    berth_category_management_read: '/berth-category-management',
    berth_category_management_update: '/berth-category-management',
    berth_category_management_delete: '/berth-category-management',
    berth_category_read: '/berth-category-management',
    berth_category_update: '/berth-category-management',
    berth_category_delete: '/berth-category-management',

    // Module: Booking Management
    berth_booking_management_create: '/booking-management',
    berth_booking_management_read: '/booking-management',
    berth_booking_management_update: '/booking-management',
    berth_booking_management_delete: '/booking-management',

    // Module: Routing Management
    routing_management_create: '/routing-management',
    routing_management_read: '/routing-management',
    routing_management_update: '/routing-management',
    routing_management_delete: '/routing-management',

    // Module: Theme Route Management
    theme_route_management_create: '/theme-routes',
    theme_route_management_read: '/theme-routes',
    theme_route_management_update: '/theme-routes',
    theme_route_management_delete: '/theme-routes',
    theme_route_management_publish: '/theme-routes',
    theme_route_management_request_approval: '/theme-routes',
    theme_route_management_approve: '/theme-routes',
    theme_route_management_reject: '/theme-routes',
    theme_route_management_revert: '/theme-routes',
    theme_route_management_add_comment: '/theme-routes',
    theme_route_management_view_comments: '/theme-routes',
    theme_route_management_view_revisions: '/theme-routes',

    // Module: Community Management
    community_management_create: '/community-management',
    community_management_read: '/community-management',
    community_management_update: '/community-management',
    community_management_delete: '/community-management',
    community_management_approve_reject_join_request: '/community-management',
    community_management_remove_block_user: '/community-management',

    // Module: Region Management
    region_management_create: '/region-management',
    region_management_read: '/region-management',
    region_management_update: '/region-management',
    region_management_delete: '/region-management',
    region_management_operator_candidates_read: '/region-management',
    region_management_dmo_candidates_read: '/region-management',

    // Module: Harbor Addon Management
    harbor_addon_management_create: '/addon-management',
    harbor_addon_management_read: '/addon-management',
    harbor_addon_management_update: '/addon-management',
    harbor_addon_management_delete: '/addon-management',

    // Module: Feedback Management
    feedback_management_create: '/user-feedbacks',
    feedback_management_read: '/user-feedbacks',
    feedback_management_update: '/user-feedbacks',
    feedback_management_delete: '/user-feedbacks',

    // Module: Berth Pier Management
    berth_pier_management_create: '/piers-management',
    berth_pier_management_read: '/piers-management',
    berth_pier_management_update: '/piers-management',
    berth_pier_management_delete: '/piers-management',

    // Module: Icons Management
    icons_management_create: '/icons-management',
    icons_management_read: '/icons-management',
    icons_management_update: '/icons-management',
    icons_management_delete: '/icons-management',

    // Module: KPI Management
    kpi_management_create: '/kpi-management',
    kpi_management_read: '/kpi-management',
    kpi_management_update: '/kpi-management',
    kpi_management_delete: '/kpi-management',

    // Module: Deployment Management
    deployment_management_create: '/deployements',
    deployment_management_read: '/deployements',
    deployment_management_update: '/deployements',
    deployment_management_delete: '/deployements',

    // Module: Organization Management
    organization_management_create: '/organization-management',
    organization_management_read: '/organization-management',
    organization_management_update: '/organization-management',
    organization_management_delete: '/organization-management',

    // Module: Club Membership Management
    read_membership_requests: '/club-requests',
    review_membership_request: '/club-requests',
    read_club_members: '/club-members',
    remove_club_member: '/club-members',

    // Module: Club Registration Management
    read_club_registration_requests: '/club-registrations',
    approve_reject_club_registration: '/club-registrations',

    // Module: Harbor Approval Requests
    read_harbor_approval_requests: '/harbors/requests',
    approve_reject_harbor_approval_request: '/harbors/requests',

    // Module: CMS
    cms_management_create: '/about-us',
    cms_management_read: '/about-us',
    cms_management_update: '/about-us',
    cms_management_delete: '/about-us',

    // Module: Service Provider Management
    service_provider_management_create: '/service-provider-management',
    service_provider_management_read: '/service-provider-management',
    service_provider_management_update: '/service-provider-management',
    service_provider_management_delete: '/service-provider-management',

    // Module: External Service
    third_party_integration_configure: '/external-services',

    // Module: DMO Management (invitations)
    dmo_invitation_management_read: '/dmo-management',
    dmo_invitation_management_create: '/dmo-management',
    dmo_invitation_management_update: '/dmo-management',
    dmo_invitation_management_delete: '/dmo-management',
    // Module: DMO Management (registrations)
    dmo_registration_management_create: '/dmo-management',
    dmo_registration_management_read: '/dmo-management',
    dmo_registration_management_update: '/dmo-management',
    dmo_registration_management_delete: '/dmo-management',

    // Module: Platform Settings
    platform_setting_read: '/platform-setting',
    platform_setting_update: '/platform-setting',
    list_value_management_create: '/list-value-management',
    list_value_management_read: '/list-value-management',
    list_value_management_update: '/list-value-management',
    list_value_management_delete: '/list-value-management',
  };

  const moduleToRouteMap: Record<string, string> = {
    role_and_permission: '/roles-management',
    activity_log: '/user-logs',
    harbor_management: '/habor-management',
    user_management: '/user-management',
    subscription_management: '/subscription-management',
    invoice_management: '/subscription-transactions',
    club_management: '/club-management',
    berth_management: '/berth-management',
    berth_booking_management: '/booking-management',
    sea_route_management: '/routing-management',
    community_management: '/community-management',
    region_management: '/region-management',
    poi_management: '/poi-management',
    harbor_addon_management: '/addon-management',
    feedback_management: '/user-feedbacks',
    boat_management: '/boat-management',
    berth_pier_management: '/piers-management',
    icons_management: '/icons-management',
    kpi_management: '/kpi-management',
    deployment_management: '/deployements',
    organization_management: '/organization-management',
    berth_category_management: '/berth-category-management',
    routing_management: '/routing-management',
    club_membership_management: '/club-members',
    club_registration_management: '/club-registrations',
    harbor_approval_request: '/harbors/requests',
    cms: '/about-us',
    email_template: '/',
    service_provider_management: '/service-provider-management',
    platform_setting: '/platform-setting',
    list_value_management: '/list-value-management',
    entitlement_grant_management: '/configuration/entitlement-grants',
  };

  const routeFromPermission = permissionToRouteMap[permissionKey];
  if (routeFromPermission) {
    return routeFromPermission;
  }

  return moduleToRouteMap[moduleKey] || null;
};

export const initializeRolePermissions = (
  apiPermissions: UserPermissionsResponse
): void => {
  const mappedPermissions = mapApiPermissionsToRolePermissions(apiPermissions);

  const hasRolePermissions = apiPermissions.some((role) =>
    role.role_has_permissions.some(
      (perm) => perm.permissions?.module?.unique_key === 'role_and_permission'
    )
  );

  if (hasRolePermissions) {
    Object.keys(mappedPermissions).forEach((role) => {
      if (!mappedPermissions[role].includes('/roles-management')) {
        mappedPermissions[role].push('/roles-management');
      }
    });
  }

  updateRolePermissions(mappedPermissions);
};
